import React, { FC, useCallback, useState, useContext, useEffect, ChangeEvent } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { useEditor } from '@craftjs/core'

import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Box from '@mui/material/Box'

import Window from '../dialog/Window'
import TinyButton from '../widgets/TinyButton'
import SmallText from '../widgets/SmallText'
import EditorContext from './EditorContext'
import Library from './Library'
import CellEditor from './CellEditor'
import useEditorActions from './useEditorActions'
import icons from '../../icons'
import ItemToolbar from './ItemToolbar'
import SaveButton from './SaveButton'

import LockIcon from '@mui/icons-material/Lock'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'

import {
  extractSetValue,
} from './utils'

import {
  ILayout,
} from '@kaidam/fc-shared/src/types/layout'

import {
  LAYOUT_NODE_TYPE_CELL,
  LAYOUT_NODE_TYPE_ROW,
  LAYOUT_CELL_TYPE_EMPTY,
} from '@kaidam/fc-shared/src/constants'

interface LayoutSidebarProps {
  onChange: {
    (data: ILayout): void,
  },
}

const LayoutSidebar: FC<React.PropsWithChildren<LayoutSidebarProps>> = ({
  onChange,
}) => {

  const editorContext = useContext(EditorContext)
  const [ deletingId, setDeletingId ] = useState('')
  
  const {
    deleteNode,
    toggleLocked,
  } = useEditorActions()

  const {
    actions,
    selected,
    title,
    type,
    locked,
  } = useEditor((state) => {
    const selectedId = extractSetValue(state.events.selected)
    let selected = ''
    let title = ''
    let type = ''
    let locked = false
    if(selectedId) {
      const node = state.nodes[selectedId]
      if(node) {
        const props = node.data.props || {}
        if(node.data.displayName == LAYOUT_NODE_TYPE_CELL) {
          if(node.data.props.type != LAYOUT_CELL_TYPE_EMPTY) {
            selected = selectedId
            title = props.type || 'unknown'
            type = LAYOUT_NODE_TYPE_CELL
            locked = props.config ? (props.config.locked || false) : false
          }
        }
        else if(node.data.displayName == LAYOUT_NODE_TYPE_ROW) {
          selected = selectedId
          title = 'row'
          type = LAYOUT_NODE_TYPE_ROW
          locked = props.config ? (props.config.locked || false) : false
        }
      }
    }
    return {
      selected,
      title,
      type,
      locked,
    }
  })

  useEffect(() => {
    const event = editorContext.editorEvent
    if(!event) return
    if(!selected) return
    if(event.id != selected) return
    if(event.action == 'delete') {
      setDeletingId(event.id)
    }
  }, [
    editorContext.editorEvent,
  ])

  const onToggleLocked = () => {
    if(!selected) return
    toggleLocked(selected)
    // actions.setProp(selected, props => {
    //   props.config = Object.assign({}, props.config, {
    //     locked: locked ? false : true
    //   })
    // })
  }

  const onDelete = useCallback(() => {
    setDeletingId('')
    deleteNode(selected)
  }, [
    selected,
  ])
  
  return (
    <Box>
      {
        selected ? (
          <Box>
            <Box
              className='titleContainer'
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                '& .title': {
                  flexGrow: 1,
                },
                '& .button': {
                  flexGrow: 0,
                },
              }}
            >
              <ItemToolbar
                withInitialDivider={ false }
                onChange={ onChange }
              />
            </Box>
            {
              type == LAYOUT_NODE_TYPE_CELL && (
                <Box>
                  {
                    editorContext.options.lockedCells && locked ? (
                      <SmallText>This item is locked</SmallText>
                    ) : (
                      <CellEditor
                        onChange={ onChange }
                      />
                    )
                  }
                </Box>
              )
            }
            {
              !editorContext.options.disabledLockedCells && !editorContext.options.lockedCells && (
                <Box
                  className='row'
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <TinyButton
                    className='lockButton'
                    variant="outlined"
                    onClick={ onToggleLocked }
                    sx={{
                      mr: 2,
                    }}
                  >
                    { locked ? 'unlock content' : 'lock content' }
                  </TinyButton>
                  <FormControlLabel
                    sx={{
                      fontSize: '0.8em',
                      color: theme => theme.palette.grey[500],
                    }}
                    label={ locked ? 'locked' : 'editable' }
                    control={
                      <Checkbox
                        icon={
                          <LockOpenOutlinedIcon />
                        }
                        checkedIcon={
                          <LockIcon />
                        }
                        checked={ locked ? true : false }
                        onChange={ onToggleLocked }
                      />
                    }
                  />
                </Box>
              )
            }
            {
              deletingId && (
                <Window
                  open
                  size="sm"
                  withCancel
                  onCancel={ () => setDeletingId('') }
                  onSubmit={ onDelete }
                  submitTitle="Delete"
                >
                  Are you sure you want to delete this item?
                </Window>
              )
            }
          </Box>
        ) : editorContext.options.disableAdd ? null : (
          <Library />
        )
      }
    </Box>
  )

}

export default LayoutSidebar